import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { UserModel } from '../_models/user.model';
import { AuthModel } from '../_models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { CacheService, DataService } from 'core';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `UserData`;
  private otpLocalStorage: string = 'isOTPVerified';

  // public fields
  currentUser$: Observable<UserModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserModel>;
  isLoadingSubject: BehaviorSubject<boolean>;


  get currentUserValue(): any {
    return JSON.parse(
      localStorage.getItem(this.authLocalStorageToken)
    );
  }

  set currentUserValue(user: any) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    private dataService: DataService,
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserModel>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }

  getTabEntityMapping() {
    if (CacheService.get("TabEntityMapping")) {
      return CacheService.get("TabEntityMapping");
    }
    else {
      return this.dataService.getByWebAction('tab/TabEntityMapping').subscribe(response => {
        CacheService.set("TabEntityMapping", response);
      });
    }
  }

  getLoggedInUserPrivileges() {
    const savedPrivileges = CacheService.get('LoggedInUserPrivileges');
    if (savedPrivileges) {
      return of(savedPrivileges);
    }
    this.dataService.getByWebAction('User/GetCurrentUserprivileges').subscribe(response => {
      CacheService.set('LoggedInUserPrivileges', JSON.stringify(this.beautifyPrivileges(response.data)));
      localStorage.setItem('LoggedInUserPrivileges', JSON.stringify(this.beautifyPrivileges(response.data)));
      return JSON.stringify(this.beautifyPrivileges(response.data));
    });
  }

  // public methods
  login(email: string, password: string): Observable<UserModel> {
    var LoginFormData = {
      username: email,
      password: password
    }
    this.isLoadingSubject.next(true);
    return this.dataService.postDataWithParam(LoginFormData)
      .map(response => {
        if (response) {
          localStorage.setItem(this.authLocalStorageToken, JSON.stringify(response));
          localStorage.setItem(this.otpLocalStorage, 'false');
          //this.getTabEntityMapping();
          //this.getLoggedInUserPrivileges();
          return response;
        }
      });
    // this.isLoadingSubject.next(true);
    // return this.authHttpService.login(email, password).pipe(
    //   map((auth: AuthModel) => {
    //     const result = this.setAuthFromLocalStorage(auth);
    //     return result;
    //   }),
    //   switchMap(() => this.getUserByToken()),
    //   catchError((err) => {
    //     console.error('err', err);
    //     return of(undefined);
    //   }),
    //   finalize(() => this.isLoadingSubject.next(false))
    // );
  }

  logout() {
    localStorage.removeItem(this.authLocalStorageToken);
    localStorage.removeItem(this.otpLocalStorage);
    localStorage.removeItem('LoggedInUserPrivileges');
    CacheService.delete('LoggedInUserPrivileges');
    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<UserModel> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.accessToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.accessToken).pipe(
      map((user: UserModel) => {
        if (user) {
          this.currentUserSubject = new BehaviorSubject<UserModel>(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user then login
  registration(user: UserModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth accessToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.accessToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel {
    try {
      const authData = JSON.parse(
        localStorage.getItem(this.authLocalStorageToken)
      );
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  beautifyPrivileges = function (data) {
    this.Role = function (_id, _name) {
      const vm = this;
      vm.id = _id;
      vm.name = _name;
    }

    this.Privilege = function (_id, _name) {
      const vm = this;
      vm.id = _id;
      vm.name = _name;
    }

    this.Entity = function (_id, _name) {
      const vm = this;
      vm.id = _id;
      vm.name = _name;
      vm.privileges = {};
    }

    this.ViewEntity = function (_key, _value) {
      const vm = this;
      vm.key = _key;
      vm.value = _value;
    }
    let bData = {
      roles: {},
      entities: {},
      tabs:{},
      viewEntities: {}
    };
    for (let i in data) {
      if (data.hasOwnProperty(i)) {
        bData.roles[data[i].RoleId] = new this.Role(data[i].RoleId, data[i].RoleName);
        for (let j = 0; j < data[i].Entity.length; j++) {
          let entity = data[i].Entity[j];
          bData.entities[entity.EntityName] = new this.Entity(entity.EntityId, entity.EntityName);
          for (let k = 0; k < entity.Privileges.length; k++) {
            let privilege = entity.Privileges[k];
            bData.entities[entity.EntityName].privileges[privilege.PrivilegeName]
              = new this.Privilege(privilege.PrivilegesId, privilege.PrivilegeName);
          }
        }
        if(data[i].Tab && data[i].Tab.length>0){
        for (let j = 0; j < data[i].Tab.length; j++) {
          let tab = data[i].Tab[j];
          bData.tabs[tab.TabId] = new this.Entity(tab.TabId, tab.TabName);
                    for (let k = 0; k < tab.Privileges.length; k++) {
                        let privileges = tab.Privileges[k];
                        bData.tabs[tab.TabId].privileges[privileges.PrivilegeName] 
                        = new this.Privilege(privileges.PrivilegesId, privileges.PrivilegeName);
                    }
      }
    }
        if (data[i].ViewEntities && data[i].ViewEntities.length > 0) {
          for (let j = 0; j < data[i].ViewEntities.length; j++) {
            let viewEntity = data[i].ViewEntities[j];
            bData.viewEntities[viewEntity.Key] = new this.ViewEntity(viewEntity.Key, viewEntity.Value);
          }
        }
      }
    }
    return bData;
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
